import { get, post, del } from '../httpMethods';
import { ApiResponse, PaginationMeta } from '../../types/api';

export type RiskFundSourceType = 'INVOICE' | 'DIAGNOSTIC_RECEIPT' | 'ADMISSION';

export interface RiskFundPatient {
  id?: string;
  firstName?: string;
  lastName?: string;
  mobileNumber?: string;
  registrationNumber?: string;
}

export interface RiskFundEntry {
  id: string;
  patientId: string;
  patient?: RiskFundPatient | null;
  sourceType: RiskFundSourceType;
  sourceId: string;
  /** The invoice / receipt number; null for an admission (no document number). */
  sourceNumber?: string | null;
  amount: number | string;
  reason?: string | null;
  movedAt: string;
  createdById: string;
  movedByName?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface RiskFundSummary {
  total: number;
  count: number;
  opd: number;
  ipd: number;
  thisMonth: number;
  thisMonthCount: number;
  today: number;
}

export interface RiskFundFilters {
  search?: string;
  sourceType?: RiskFundSourceType | '';
  dateFrom?: string;
  dateTo?: string;
}

export interface MoveToRiskFundPayload {
  receiptIds?: string[];
  admissionIds?: string[];
  invoiceIds?: string[];
  reason?: string;
  /**
   * Amount to write off. Omit to write off the full outstanding balance. Only
   * valid when exactly one document is targeted.
   */
  amount?: number;
}

export interface RiskFundSkippedEntry {
  sourceType: RiskFundSourceType;
  sourceId: string;
  reason: string;
}

export interface MoveToRiskFundResult {
  moved: RiskFundEntry[];
  skipped: RiskFundSkippedEntry[];
  totalAmount: number;
}

export interface RiskFundDeleteResult {
  deleted: string[];
  notFound: string[];
}

export const riskFundService = {
  getEntries: (
    page = 1,
    limit = 20,
    filters: RiskFundFilters = {},
  ): Promise<ApiResponse<RiskFundEntry[]> & { meta: PaginationMeta }> => {
    const params = new URLSearchParams({
      page: String(page),
      limit: String(limit),
    });
    if (filters.search) params.set('search', filters.search);
    if (filters.sourceType) params.set('sourceType', filters.sourceType);
    if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
    if (filters.dateTo) params.set('dateTo', filters.dateTo);

    return get<RiskFundEntry[]>(
      `/billing/risk-fund?${params.toString()}`,
    ) as Promise<ApiResponse<RiskFundEntry[]> & { meta: PaginationMeta }>;
  },

  getSummary: (): Promise<ApiResponse<RiskFundSummary>> => {
    return get<RiskFundSummary>('/billing/risk-fund/summary');
  },

  getEntry: (id: string): Promise<ApiResponse<RiskFundEntry>> => {
    return get<RiskFundEntry>(`/billing/risk-fund/${id}`);
  },

  /**
   * Writes off receipts, admissions or invoices. Ineligible documents come back
   * in `skipped` instead of failing the whole batch.
   */
  moveToRiskFund: (
    payload: MoveToRiskFundPayload,
  ): Promise<ApiResponse<MoveToRiskFundResult>> => {
    return post<MoveToRiskFundResult>('/billing/risk-fund/move', payload);
  },

  /** Reverses the write-off and removes the audit entry. */
  deleteEntry: (id: string): Promise<ApiResponse<RiskFundDeleteResult>> => {
    return del<RiskFundDeleteResult>(`/billing/risk-fund/${id}`);
  },

  bulkDeleteEntries: (ids: string[]): Promise<ApiResponse<RiskFundDeleteResult>> => {
    return post<RiskFundDeleteResult>('/billing/risk-fund/bulk-delete', { ids });
  },
};
